import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button as MuiButton,
    Stack
} from '@mui/material';
import {
    Upload as UploadIcon,
    FileCopy,
    Refresh
} from '@mui/icons-material';
import { DocumentSummary as SummaryData } from "@my-helpers/common";
import Search from '@/components/Search';
import ListTitle from '@/components/ListTitle';
import { useTheme } from '@mui/material/styles';
import DocumentSummary from '@/components/document/Summary';
import { Notification } from '@/components/Notification';
import { Button } from '@/components/Button';
import { useDocuments } from '@my-helpers/common';
import { Upload } from '@/components/document/Upload';

const DocumentsView: React.FC = () => {
    const { view, command } = useDocuments();
    const { isLoading, error, documents } = view;
    const { clearError } = command;
    const navigate = useNavigate();
    const theme = useTheme();
    const [searchTerm, setSearchTerm] = useState('');
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [documentToDelete, setDocumentToDelete] = useState<SummaryData | null>(null);
    const [uploading, setUploading] = useState(false);

    const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(event.target.value);
    };

    const handleSearchSubmit = () => {
        if (searchTerm.trim()) {
            command.searchDocuments({
                query_type: 'byContext',
                context: searchTerm.trim()
            });
        }
    };

    const handleKeyPress = (event: React.KeyboardEvent) => {
        if (event.key === 'Enter') {
            handleSearchSubmit();
        }
    };

    const handleViewDocument = (document: SummaryData) => {
        if (document.id) {
            navigate(`/documents/${document.id}`);
        }
    };

    const handleDeleteClick = (document: SummaryData) => {
        setDocumentToDelete(document);
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (!documentToDelete?.id) return;

        const success = await command.deleteDocument(documentToDelete.id);
        if (success) {
            setDeleteDialogOpen(false);
            setDocumentToDelete(null);
        }
    };

    const handleDeleteCancel = () => {
        setDeleteDialogOpen(false);
        setDocumentToDelete(null);
    };

    if (uploading) {
        return <Upload
            isLoading={isLoading}
            error={error}
            clearError={clearError}
            createDocument={command.createDocument}
            setUploading={setUploading}
        />;
    }

    return (
        <Stack padding={theme.spacing(3)}>
            <Box display="flex" alignItems="center" gap={2} mb={3}>
                <Box>
                    <ListTitle title="Documents" icon={FileCopy} />
                </Box>
                <Box flexGrow={1} minWidth={'200px'}>
                    <Search
                        placeholder="Search documents..."
                        sx={{
                            width: '100%',
                            minWidth: 'inherit'
                        }}
                    />
                </Box>
                <Box display="flex"
                    flexDirection="row"
                    justifyContent="flex-end"
                    gap={1}
                    padding={theme.spacing(0, 0, 0, 2)}
                >
                    <Button
                        variant="secondary"
                        icon={<Refresh />}
                        tooltip="Refresh documents"
                        onClick={() => command.refreshDocuments()}
                    />
                    <Button
                        variant="secondary"
                        icon={<UploadIcon />}
                        tooltip="Upload document"
                        onClick={() => setUploading(true)}
                    />
                </Box>
            </Box>

            <Box display="flex"
                flexDirection="column"
                alignItems="center"
                gap={1}>
                {
                    documents?.map((document: SummaryData) => (
                        <DocumentSummary
                            key={document.id}
                            document={document}
                            handleViewDocument={handleViewDocument}
                            handleDeleteClick={handleDeleteClick}
                        />
                    ))
                }
            </Box>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
                <DialogTitle>Delete Document</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to delete "{documentToDelete?.file_name}"?
                        This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <MuiButton
                        onClick={handleDeleteCancel}
                        color="primary"
                        variant="outlined">
                        Cancel
                    </MuiButton>
                    <MuiButton
                        onClick={handleDeleteConfirm}
                        color="error"
                        variant="contained">
                        Delete
                    </MuiButton>
                </DialogActions>
            </Dialog>

            <Notification
                isLoading={isLoading}
                error={error}
                delay={300}
                autoHideDuration={1200}
                clearError={clearError}
            />
        </Stack>
    );
};

export default DocumentsView;
